/**
 * Logs the user out after a period of inactivity.
 *
 * The last-activity timestamp lives in localStorage so activity in any open
 * tab keeps every tab's session alive, and a tab reopened after the timeout
 * is caught immediately. Activity writes are throttled so mousemove/scroll
 * don't hammer localStorage.
 */

export const IDLE_TIMEOUT_MS = 60 * 60 * 1000 // 1 hour (tradies)

// Business admins and web supervisors see company-wide data, so they get a
// shorter leash (per product: 30 minutes of inactivity).
export const ADMIN_IDLE_TIMEOUT_MS = 30 * 60 * 1000

const ACTIVITY_KEY = 'LAST_ACTIVITY_AT'
const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
]
const WRITE_THROTTLE_MS = 5000
const CHECK_INTERVAL_MS = 30000

function readLastActivity() {
  const raw = localStorage.getItem(ACTIVITY_KEY)
  const ts = raw ? parseInt(raw, 10) : NaN
  return Number.isFinite(ts) ? ts : null
}

/**
 * Forgets the stored last-activity time. Call on a fresh login: a timestamp
 * left over from a previous session (tab closed without signing out) would
 * otherwise count as idle and sign the user straight back out.
 */
export function resetIdleClock() {
  localStorage.removeItem(ACTIVITY_KEY)
}

/**
 * Starts idle tracking. Returns a stop() function; call it when the layout
 * that started tracking is destroyed.
 *
 * @param {{ onIdle: Function, timeoutMs?: number }} options
 */
export function startIdleLogout({ onIdle, timeoutMs = IDLE_TIMEOUT_MS }) {
  let stopped = false
  let lastWrite = 0

  const touch = () => {
    const now = Date.now()
    if (now - lastWrite < WRITE_THROTTLE_MS) return
    lastWrite = now
    localStorage.setItem(ACTIVITY_KEY, String(now))
  }

  const isIdle = () => {
    const last = readLastActivity()
    // No timestamp (first run, or cleared storage) counts as fresh activity —
    // the user just loaded the page.
    if (last === null) return false
    return Date.now() - last >= timeoutMs
  }

  const check = () => {
    if (stopped) return
    if (isIdle()) {
      stop()
      onIdle()
    }
  }

  // Returning to a hidden tab should log out immediately if the timeout has
  // passed, rather than waiting for the next interval tick. If it hasn't,
  // switching back to the tab is itself activity.
  const onVisible = () => {
    if (document.hidden) return
    check()
    if (!stopped) touch()
  }

  function stop() {
    if (stopped) return
    stopped = true
    clearInterval(interval)
    ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, touch))
    document.removeEventListener('visibilitychange', onVisible)
  }

  // A timestamp already stale when tracking starts (tab reopened after being
  // closed, browser session restore, PC woke from sleep) must be honoured
  // right away — otherwise the touch() below unconditionally stamps "now"
  // and the real idle gap is erased before any check ever runs.
  if (isIdle()) {
    onIdle()
    return () => {}
  }

  const interval = setInterval(check, CHECK_INTERVAL_MS)
  ACTIVITY_EVENTS.forEach((event) =>
    window.addEventListener(event, touch, { passive: true }),
  )
  document.addEventListener('visibilitychange', onVisible)

  touch()
  return stop
}
